import type { TabState } from '../../types';
import { getFiltered } from '../../utils/filter';
import { fmtG } from '../../utils/format';
import { MetricCard } from '../ui/MetricCard';
import { SectionHeader } from '../ui/SectionHeader';
import { OfferingChart } from '../charts/OfferingChart';
import { ALL_DATA } from '../../data/deals';
import React from 'react';
interface Props {
  state: TabState;
  searchRows?: React.ReactNode;
}
function regionBadge(region: string) {
  const styles: Record<string, {
    bg: string;
    color: string;
  }> = {
    AMER: {
      bg: '#EFF6FF',
      color: '#1D4ED8'
    },
    EMEA: {
      bg: '#F5F3FF',
      color: '#7C3AED'
    },
    JAPAC: {
      bg: '#F0FDF4',
      color: '#15803D'
    }
  };
  const s = styles[region] || {
    bg: '#F9FAFB',
    color: '#374151'
  };
  return <span className="badge" style={{
    background: s.bg,
    color: s.color,
    fontSize: 8
  }} data-id="element-165">{region}</span>;
}
export function SDPanel({
  state,
  searchRows
}: Props) {
  const all = getFiltered('sd', state);
  const ip = all.filter(d => d.status === 'In Progress');
  const aged = ip.filter(d => d.age > 90);
  const scs = new Set(ip.map(d => d.sc)).size;
  const oppGnarr = all.reduce((s, d) => s + (d.opp_gnarr || 0), 0);

  // Closed Booked GNARR: won deals (adjusted_commit === 'Won') matching region/sub/CQ
  const wonDeals = ALL_DATA.filter(d => {
    if (d.adjusted_commit !== 'Won') return false;
    if (state.r !== 'Global' && d.region !== state.r) return false;
    if (state.sub && state.sub !== 'All' && d.sub_region !== state.sub) return false;
    return true;
  });
  const wonGnarr = wonDeals.reduce((s, d) => s + (d.opp_gnarr || 0), 0);
  const wonCount = wonDeals.length;
  const agedSorted = [...aged].sort((a, b) => b.age - a.age).slice(0, 8);
  const dealsSorted = [...ip].sort((a, b) => b.age - a.age);
  return <div data-id="element-166">
      {/* Metrics */}
      <div className="mrow" data-id="element-167">
        <MetricCard label="In-progress" value={ip.length} sub={`${scs} SCs active`} colorClass="c-blue" data-id="element-168" />
        <MetricCard label="Aged >90 days" value={aged.length} sub="need immediate action" colorClass="c-red" data-id="element-169" />
        <MetricCard label="Closed Booked GNARR" value={fmtG(wonGnarr)} sub={`${wonCount} won opportunit${wonCount === 1 ? 'y' : 'ies'}`} colorClass="c-purple" data-id="element-170" />
        <MetricCard label="GNARR Influenced" value={fmtG(oppGnarr)} sub="total opp GNARR (created qtr)" colorClass="c-green" data-id="element-171" />
      </div>

      {/* Offering chart + Aged deals */}
      <div className="row2" data-id="element-172">
        <div className="card" data-id="element-173">
          <h3 data-id="element-174">Requests by solution</h3>
          <OfferingChart deals={all} data-id="element-175" />
        </div>
        <div className="card" data-id="element-176">
          <h3 data-id="element-177">Aged deals (&gt;90 days) at risk</h3>
          {agedSorted.length > 0 ? <table className="tbl" data-id="element-178">
              <thead data-id="element-179">
                <tr data-id="element-180">
                  <th style={{
                width: '30%'
              }} data-id="element-181">Account</th>
                  <th style={{
                width: '20%'
              }} data-id="element-182">SC</th>
                  <th style={{
                width: '12%'
              }} data-id="element-183">Age</th>
                  <th style={{
                width: '18%'
              }} data-id="element-184">GNARR</th>
                  <th style={{
                width: '12%'
              }} data-id="element-185">Close Q</th>
                  <th style={{
                width: '8%'
              }} data-id="element-186">Sub</th>
                </tr>
              </thead>
              <tbody data-id="element-187">
                {agedSorted.map(d => <tr key={d.id} data-id="element-188">
                    <td title={d.account} data-id="element-189">{d.account.substring(0, 22)}</td>
                    <td title={d.sc} data-id="element-190">{d.sc.split(' ')[0]}</td>
                    <td className="c-red" style={{
                fontWeight: 600
              }} data-id="element-191">{d.age}d</td>
                    <td data-id="element-192">{fmtG(d.gnarr)}</td>
                    <td data-id="element-193">{d.close_q}</td>
                    <td data-id="element-194"><span className="badge b-gray" style={{
                  fontSize: 8
                }} data-id="element-195">{d.sub_region}</span></td>
                  </tr>)}
              </tbody>
            </table> : <div style={{
          padding: '20px',
          textAlign: 'center',
          color: '#8892a0',
          fontSize: 11
        }} data-id="element-196">No aged deals in this filter ✓</div>}
        </div>
      </div>

      {/* Full deals list */}
      <SectionHeader title="In-progress deals — full list" data-id="element-197" />
      <div className="tw" data-id="element-198">
        <table className="tbl" data-id="element-199">
          <thead data-id="element-200">
            <tr data-id="element-201">
              <th style={{
              width: '11%'
            }} data-id="element-202">DR ID</th>
              <th style={{
              width: '22%'
            }} data-id="element-203">Account</th>
              <th style={{
              width: '15%'
            }} data-id="element-204">SC Assigned</th>
              <th style={{
              width: '14%'
            }} data-id="element-205">Offering</th>
              <th style={{
              width: '7%'
            }} data-id="element-206">Age</th>
              <th style={{
              width: '10%'
            }} data-id="element-207">GNARR</th>
              <th style={{
              width: '9%'
            }} data-id="element-208">Close Q</th>
              <th style={{
              width: '7%'
            }} data-id="element-209">Region</th>
              <th style={{
              width: '5%'
            }} data-id="element-210">Sub</th>
            </tr>
          </thead>
          <tbody data-id="element-211">
            {searchRows ?? dealsSorted.map(d => <tr key={d.id} data-id="element-212">
                <td data-id="element-213"><span style={{
                fontSize: 9,
                color: '#8892a0'
              }} data-id="element-214">{d.id}</span></td>
                <td title={d.account} data-id="element-215">{d.account.substring(0, 24)}</td>
                <td title={d.sc} data-id="element-216">{d.sc.split(' ').slice(0, 2).join(' ')}</td>
                <td data-id="element-217">
                  <span className="badge b-blue" style={{
                fontSize: 8
              }} data-id="element-218">
                    {d.offering.replace('Engagement', 'Eng.').replace('Support', 'Sup.')}
                  </span>
                </td>
                <td className={d.age > 90 ? 'c-red' : d.age > 45 ? 'c-gold' : ''} style={{
              fontWeight: d.age > 90 ? 600 : 400
            }} data-id="element-219">{d.age}d</td>
                <td data-id="element-220">{fmtG(d.gnarr)}</td>
                <td data-id="element-221">{d.close_q}</td>
                <td data-id="element-222">{regionBadge(d.region)}</td>
                <td data-id="element-223"><span className="badge b-gray" style={{
                fontSize: 8
              }} data-id="element-224">{d.sub_region}</span></td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}